import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, Loader2, X, Type, DollarSign } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import VideoUploader from "@/components/VideoUploader";
import CountryCopyDrawer from "@/components/CountryCopyDrawer";
import MembershipPricingDrawer from "@/components/MembershipPricingDrawer";
import api from "@/lib/api";
import { getCountry } from "@/i18n/countries";

/**
 * Admin — country activation console.
 *
 * Every SUPPORTED country is listed with its currency, offered
 * languages, and an active toggle. Flipping the toggle immediately
 * PATCHes `/api/admin/countries/{code}` — no explicit save button, and
 * the switch reverts on failure via optimistic-update rollback.
 *
 * The public `/api/public/countries` endpoint reflects these toggles
 * within one request cycle, so the country switcher on the marketing
 * site + inside portals updates on the next page load.
 */
export default function AdminCountries() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingCode, setSavingCode] = useState(null);
    // Country whose hero-copy drawer is open (null = closed).
    const [copyDrawer, setCopyDrawer] = useState(null);
    // Country whose membership-pricing drawer is open.
    const [pricingDrawer, setPricingDrawer] = useState(null);

    useEffect(() => {
        api.get("/admin/countries")
            .then((r) => setRows(r.data.countries || []))
            .catch(() => toast.error("Couldn't load countries"))
            .finally(() => setLoading(false));
    }, []);

    const toggle = async (code, next) => {
        const prevRows = rows;
        setRows((rs) => rs.map((r) => (r.code === code ? { ...r, active: next } : r)));
        setSavingCode(code);
        try {
            const { data } = await api.patch(`/admin/countries/${code}`, { active: next });
            setRows((rs) => rs.map((r) => (r.code === code ? { ...r, ...data } : r)));
            toast.success(
                `${code} ${next ? "activated" : "deactivated"} — public switcher will update on next reload.`
            );
        } catch (err) {
            setRows(prevRows); // rollback
            toast.error(`Couldn't toggle ${code}. Please retry.`);
        } finally {
            setSavingCode(null);
        }
    };

    // Save (or clear) a per-country hero video. Pass `null` to clear.
    const saveHeroVideo = async (code, url) => {
        setSavingCode(code);
        try {
            const { data } = await api.patch(`/admin/countries/${code}`, {
                hero_video_url: url || "",
            });
            setRows((rs) => rs.map((r) => (r.code === code ? { ...r, ...data } : r)));
            toast.success(url ? `${code} hero video updated` : `${code} hero video cleared`);
        } catch (err) {
            toast.error(`Couldn't save hero video for ${code}`);
        } finally {
            setSavingCode(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-navy-500 py-16 justify-center">
                <Loader2 className="animate-spin" size={16} />
                Loading countries…
            </div>
        );
    }

    const activeCount = rows.filter((r) => r.active).length;

    return (
        <div className="space-y-6" data-testid="admin-countries-page">
            <header className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                        Multi-country
                    </div>
                    <h1 className="font-display text-4xl text-navy-700">Countries</h1>
                    <p className="text-navy-500 mt-2 max-w-xl leading-relaxed">
                        Toggle a country ON to publish it to the public site + portals.
                        Deactivated countries disappear from the switcher, and any visitor
                        whose cached market is turned off is snapped to the default (Hong
                        Kong) on their next request.
                    </p>
                </div>
                <div className="rounded-2xl border border-navy-100 bg-cream px-5 py-3 flex-shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-navy-500">
                        Active
                    </div>
                    <div
                        data-testid="admin-countries-active-count"
                        className="font-display text-3xl text-navy-700"
                    >
                        {activeCount}
                        <span className="text-base text-navy-400"> / {rows.length}</span>
                    </div>
                </div>
            </header>

            <div className="rounded-2xl border border-navy-100 bg-white overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-cream/60 text-[11px] uppercase tracking-widest text-navy-500">
                        <tr>
                            <th className="text-left px-5 py-3">Country</th>
                            <th className="text-left px-5 py-3">Currency</th>
                            <th className="text-left px-5 py-3">Languages</th>
                            <th className="text-left px-5 py-3">Hero video</th>
                            <th className="text-left px-5 py-3">Hero copy</th>
                            <th className="text-left px-5 py-3">Membership</th>
                            <th className="text-left px-5 py-3">Updated</th>
                            <th className="text-right px-5 py-3">Active</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-navy-100">
                        {rows.map((r) => {
                            const localMeta = getCountry(r.code);
                            const isSaving = savingCode === r.code;
                            return (
                                <tr
                                    key={r.code}
                                    data-testid={`admin-country-row-${r.code}`}
                                    className="hover:bg-cream/30 transition-colors"
                                >
                                    <td className="px-5 py-4">
                                        <div className="flex items-center gap-3">
                                            <span className="text-2xl leading-none">
                                                {localMeta.flag}
                                            </span>
                                            <div>
                                                <div className="font-medium text-navy-700">
                                                    {r.name}
                                                </div>
                                                <div className="text-[11px] uppercase tracking-widest text-navy-400">
                                                    {r.code}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 text-navy-600 font-mono text-xs uppercase tracking-widest">
                                        {r.currency}
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex flex-wrap gap-1">
                                            {localMeta.languages.map((l) => (
                                                <span
                                                    key={l}
                                                    className="text-[11px] px-2 py-0.5 rounded-full bg-navy-50 text-navy-600 border border-navy-100"
                                                >
                                                    {l}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex items-center gap-2">
                                            {r.hero_video_url ? (
                                                <>
                                                    <video
                                                        src={r.hero_video_url}
                                                        muted
                                                        loop
                                                        playsInline
                                                        autoPlay
                                                        data-testid={`admin-country-hero-preview-${r.code}`}
                                                        className="w-12 h-12 rounded-lg object-cover border border-navy-100 bg-white pointer-events-none flex-shrink-0"
                                                    />
                                                    <div className="flex flex-col gap-1">
                                                        <VideoUploader
                                                            testid={`admin-country-hero-upload-${r.code}`}
                                                            label="Replace"
                                                            size="sm"
                                                            onUploaded={(url) => saveHeroVideo(r.code, url)}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => saveHeroVideo(r.code, null)}
                                                            disabled={isSaving}
                                                            data-testid={`admin-country-hero-clear-${r.code}`}
                                                            className="inline-flex items-center gap-1 text-[11px] text-navy-500 hover:text-red-600 transition-colors"
                                                        >
                                                            <X size={11} /> Clear
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex flex-col items-start gap-1">
                                                    <span
                                                        data-testid={`admin-country-hero-empty-${r.code}`}
                                                        className="text-[11px] text-navy-400 italic"
                                                    >
                                                        Default (HK panda)
                                                    </span>
                                                    <VideoUploader
                                                        testid={`admin-country-hero-upload-${r.code}`}
                                                        label="Upload"
                                                        size="sm"
                                                        onUploaded={(url) => saveHeroVideo(r.code, url)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCopyDrawer(r)}
                                            data-testid={`admin-country-copy-btn-${r.code}`}
                                            className="rounded-full border-navy-100 text-navy-700 hover:bg-navy-50 h-8 text-xs"
                                        >
                                            <Type size={12} className="mr-1.5" />
                                            Edit copy
                                        </Button>
                                    </td>
                                    <td className="px-5 py-4">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setPricingDrawer(r)}
                                            data-testid={`admin-country-pricing-btn-${r.code}`}
                                            className="rounded-full border-navy-100 text-navy-700 hover:bg-navy-50 h-8 text-xs"
                                        >
                                            <DollarSign size={12} className="mr-1.5" />
                                            Set prices
                                        </Button>
                                    </td>
                                    <td className="px-5 py-4 text-[11px] text-navy-500">
                                        {r.updated_at
                                            ? new Date(r.updated_at).toLocaleString()
                                            : "—"}
                                        {r.updated_by && (
                                            <div className="text-navy-400 mt-0.5">
                                                by {r.updated_by}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-5 py-4 text-right">
                                        <div className="inline-flex items-center gap-2">
                                            {isSaving && (
                                                <Loader2
                                                    className="animate-spin text-navy-400"
                                                    size={14}
                                                />
                                            )}
                                            <Switch
                                                data-testid={`admin-country-toggle-${r.code}`}
                                                checked={!!r.active}
                                                disabled={isSaving}
                                                onCheckedChange={(next) =>
                                                    toggle(r.code, next)
                                                }
                                                className="data-[state=checked]:bg-emerald-600 data-[state=unchecked]:bg-navy-100"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="rounded-xl border border-gold-200 bg-gold-50 px-5 py-4 flex items-start gap-3">
                <Globe className="text-gold-600 mt-0.5 flex-shrink-0" size={18} />
                <div className="text-sm text-navy-600 leading-relaxed">
                    <div className="font-semibold text-navy-700 mb-1">
                        Activating a new market
                    </div>
                    Existing deals + product groups need to include the new country in
                    their <span className="font-mono text-xs">country_codes</span> field
                    before they&apos;ll appear. Open a deal or group in the admin console
                    and tick the country in the &ldquo;Available in&rdquo; picker — or leave the
                    picker empty to publish that row to every market at once.
                </div>
            </div>

            <CountryCopyDrawer
                open={!!copyDrawer}
                onOpenChange={(v) => !v && setCopyDrawer(null)}
                country={copyDrawer}
            />

            <MembershipPricingDrawer
                open={!!pricingDrawer}
                onOpenChange={(v) => !v && setPricingDrawer(null)}
                country={pricingDrawer}
            />
        </div>
    );
}
